import { apiFetch, apiFetchBlob } from "@/lib/api";
import type { ReportResult } from "./types";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

export type ReportParams = Record<string, string | number | undefined>;

function buildQuery(params: ReportParams): string {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") q.set(key, String(value));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** Load a firm-scoped report. The backend does the aggregation + sorting; we
 * pass through the sort/filter params it validates against a per-report
 * allow-list. */
export async function fetchReport(key: string, params: ReportParams = {}): Promise<ReportResult> {
  const res = await apiFetch<Envelope<ReportResult>>(`/reports/${key}/${buildQuery(params)}`);
  return res.data;
}

/** Download the report's Excel export (same rows/sort/filters as the table). */
export async function exportReportXlsx(key: string, params: ReportParams = {}): Promise<void> {
  const blob = await apiFetchBlob(`/reports/${key}/export/${buildQuery({ ...params, format: "xlsx" })}`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${key}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ---- Per-client Tax Relief Report ----------------------------------------- //

export interface TaxReliefData {
  client: string;
  availableSections: { key: string; label: string }[];
  // Section payloads are heterogeneous (summary objects + {rows:[]}); the page
  // renders the known shapes.
  sections: Record<string, Record<string, unknown>>;
}

export async function getClientTaxRelief(clientId: string): Promise<TaxReliefData> {
  const res = await apiFetch<
    Envelope<{
      client: string;
      available_sections: { key: string; label: string }[];
      sections: Record<string, Record<string, unknown>>;
    }>
  >(`/reports/clients/${clientId}/tax-relief/`);
  return {
    client: res.data.client,
    availableSections: res.data.available_sections,
    sections: res.data.sections,
  };
}

export async function downloadTaxReliefPdf(clientId: string, sections: string[]): Promise<Blob> {
  return apiFetchBlob(`/reports/clients/${clientId}/tax-relief/pdf/`, {
    method: "POST",
    json: { sections },
  });
}

export async function saveTaxReliefPdf(
  clientId: string,
  sections: string[],
): Promise<{ documentId: string }> {
  const res = await apiFetch<Envelope<{ document_id: string }>>(
    `/reports/clients/${clientId}/tax-relief/pdf/`,
    { method: "POST", json: { sections, save: true } },
  );
  return { documentId: res.data.document_id };
}
